import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Alert } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import * as MuiIcons from '@mui/icons-material';
import { DataTable } from './DataTable';
import { StylishFormModal } from './StylishFormModal';
import { RecordDetailDrawer } from './RecordDetailDrawer';
import { PageHeader } from '../ui/PageHeader';
import { ModuleConfig } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { CORE_MODULE_KEYS } from '../../config/sidebarMenu';
import { RADIUS, brandCss } from '../../theme/appTheme';
import { getPageSubtitle } from '../../constants/pageSubtitles';
import { DEFAULT_ROWS_PER_PAGE } from '../../constants/tableDefaults';
import { useToast } from '../../contexts/ToastContext';
import { useTableColumns } from '../../hooks/useTableColumns';
import { useVehicleMasterData } from '../../hooks/useVehicleMasterData';
import api, { clientAPI, vehicleAPI, fuelAPI, assetAPI, statsAPI, vehicleVendorAPI } from '../../services/api';
import { EntityOption } from './SearchableEntitySelect';
import { isFieldVisible } from '../../utils/fieldVisibility';
import { buildConfiguredToolbarFilters } from '../../utils/tableColumnSettings';

interface CrudModulePageProps {
  config: ModuleConfig;
}

const getModuleIcon = (iconName: string) => {
  const Icon = (MuiIcons as Record<string, React.ComponentType<{ sx?: object }>>)[iconName] || MuiIcons.Folder;
  return <Icon />;
};

export const CrudModulePage: React.FC<CrudModulePageProps> = ({ config }) => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { showToast } = useToast();
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [systemTotal, setSystemTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [detailRow, setDetailRow] = useState<Record<string, unknown> | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [clientOptions, setClientOptions] = useState<EntityOption[]>([]);
  const [vehicleOptions, setVehicleOptions] = useState<EntityOption[]>([]);
  const [userOptions, setUserOptions] = useState<EntityOption[]>([]);
  const [vendorOptions, setVendorOptions] = useState<EntityOption[]>([]);
  const isCore = CORE_MODULE_KEYS.includes(config.key);
  const usesClientField = config.fields.some((f) => f.type === 'client');
  const usesVehicleField = config.fields.some((f) => f.type === 'vehicle');
  const usesUserField = config.fields.some((f) => f.type === 'user');
  const usesVendorField = config.fields.some((f) => f.type === 'vendor');
  const usesVehicleFleet = config.key === 'vehicles';
  const vehicleFleet = useVehicleMasterData(usesVehicleFleet);

  const canCreate = hasPermission(config.key, 'create');
  const canView = hasPermission(config.key, 'view');
  const canEdit = hasPermission(config.key, 'edit');
  const canDelete = hasPermission(config.key, 'delete');
  const canImport = hasPermission(config.key, 'import');
  const canExport = hasPermission(config.key, 'export');

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await api.get(config.apiPath);
      const list = Array.isArray(res.data) ? res.data : [];
      setRows(list);
      try {
        const totalRes = await statsAPI.systemTotal(config.key);
        setSystemTotal(Number(totalRes.data?.total));
      } catch {
        setSystemTotal(list.length);
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg || `Failed to load ${config.title}. Restart server on port 5001.`);
    } finally {
      setLoading(false);
    }
  }, [config.apiPath, config.title, config.key]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  useEffect(() => {
    if (!usesClientField) return;
    clientAPI.getAll().then((res) => {
      setClientOptions((res.data || []).map((c: Record<string, string>) => ({
        _id: String(c._id),
        label: c.clientName || c.companyFullName || 'Client',
        sublabel: c.companyFullName,
      })));
    }).catch(() => {});
  }, [usesClientField]);

  useEffect(() => {
    if (!usesVehicleField) return;
    vehicleAPI.getAll().then((res) => {
      setVehicleOptions((res.data || []).map((v: Record<string, string>) => ({
        _id: String(v._id),
        label: v.vehicleNumber,
        sublabel: [v.vehicleType, v.brand].filter(Boolean).join(' · '),
      })));
    }).catch(() => {});
  }, [usesVehicleField]);

  useEffect(() => {
    if (!usesUserField) return;
    fuelAPI.getEmployees().then((res) => {
      setUserOptions((res.data || []).map((u: Record<string, string>) => ({
        _id: String(u._id),
        label: u.name,
        sublabel: u.email,
      })));
    }).catch(() => {});
  }, [usesUserField]);

  useEffect(() => {
    if (!usesVendorField) return;
    vehicleVendorAPI.getAll().then((res) => {
      setVendorOptions((res.data || []).map((v: Record<string, string>) => ({
        _id: String(v._id),
        label: v.vendorName || 'Vendor',
        sublabel: [v.contactPerson, v.mobile].filter(Boolean).join(' · '),
        meta: {
          contactPerson: v.contactPerson || '',
          mobile: v.mobile || '',
          address: v.address || '',
          contractDocument: v.contractDocument || '',
          contractDate: v.contractDate ? String(v.contractDate).slice(0, 10) : '',
          status: v.status || 'active',
        },
      })));
    }).catch(() => {});
  }, [usesVendorField]);

  const resetForm = () => {
    const empty: Record<string, string> = {};
    config.fields.forEach((f) => {
      if (f.name === 'status') {
        empty[f.name] = f.options?.[0]?.value || 'active';
      } else if (f.name === 'ownerType') {
        empty[f.name] = 'owned';
      } else if (f.name === 'paymentType') {
        empty[f.name] = 'monthly_salary';
      } else if (f.name === 'restHours') {
        empty[f.name] = '10';
      } else if (f.name === 'fuelType') {
        empty[f.name] = 'Diesel';
      } else if (f.type === 'date' && config.key === 'fuel') {
        empty[f.name] = new Date().toISOString().slice(0, 10);
      } else {
        empty[f.name] = '';
      }
    });
    setFormData(empty);
    setEditId(null);
  };

  const openEdit = (row: Record<string, unknown>) => {
    const data: Record<string, string> = {};
    config.fields.forEach((f) => {
      if (f.type === 'client') {
        const client = row.client as Record<string, unknown> | string | undefined;
        data[f.name] = typeof client === 'object' && client?._id
          ? String(client._id)
          : String(row[f.name] ?? '');
      } else if (f.type === 'vehicle') {
        const vehicle = row.vehicle as Record<string, unknown> | string | undefined;
        data[f.name] = typeof vehicle === 'object' && vehicle?._id
          ? String(vehicle._id)
          : String(row[f.name] ?? '');
      } else if (f.type === 'user') {
        const employee = row.employee as Record<string, unknown> | string | undefined;
        data[f.name] = typeof employee === 'object' && employee?._id
          ? String(employee._id)
          : String(row[f.name] ?? '');
      } else if (f.name === 'typeId' || f.name === 'makeId' || f.name === 'modelId') {
        const raw = row[f.name];
        data[f.name] = raw && typeof raw === 'object' && '_id' in (raw as object)
          ? String((raw as { _id: unknown })._id)
          : raw ? String(raw) : '';
      } else if (f.name === 'ownerType') {
        const t = String(row.ownerType || 'owned');
        data[f.name] = t === 'rented' || t === 'leased' ? 'leased' : 'owned';
      } else if (f.type === 'vendor') {
        const vendor = row.vendorId as Record<string, unknown> | string | undefined;
        data[f.name] = typeof vendor === 'object' && vendor?._id
          ? String(vendor._id)
          : String(row.vendorId ?? row[f.name] ?? '');
      } else if (f.type === 'date' && row[f.name]) {
        data[f.name] = new Date(String(row[f.name])).toISOString().slice(0, 10);
      } else {
        data[f.name] = String(row[f.name] ?? '');
      }
    });
    if (config.key === 'vehicles') {
      const resolved = vehicleFleet.resolveIdsFromNames(
        String(row.vehicleType || ''),
        String(row.brand || ''),
        String(row.vehicleModel || ''),
      );
      if (!data.typeId) data.typeId = resolved.typeId;
      if (!data.makeId) data.makeId = resolved.makeId;
      if (!data.modelId) data.modelId = resolved.modelId;
    }
    setFormData(data);
    setEditId(String(row._id));
    setOpen(true);
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      setError('');
      const missing = config.fields.find(
        (f) => f.required && isFieldVisible(f, formData) && !String(formData[f.name] || '').trim(),
      );
      if (missing) {
        setError(`${missing.label} is required`);
        setSaving(false);
        return;
      }
      if (config.key === 'vehicles') {
        if (!formData.typeId || !formData.makeId || !formData.modelId) {
          setError('Please select vehicle type, make and model from the master list');
          setSaving(false);
          return;
        }
      }
      const payload: Record<string, unknown> = { ...formData };
      config.fields.forEach((f) => {
        if (f.type === 'number' && payload[f.name] !== '' && payload[f.name] !== undefined) {
          payload[f.name] = Number(payload[f.name]);
        }
        if (f.type === 'client' && !payload[f.name]) {
          payload[f.name] = null;
        }
        if ((f.type === 'vehicle' || f.type === 'user' || f.type === 'vendor') && !payload[f.name]) {
          delete payload[f.name];
        }
        if (!isFieldVisible(f, formData) && (f.name.startsWith('vendor') || f.name === 'vendorId')) {
          payload[f.name] = f.type === 'date' || f.name === 'vendorId' ? null : '';
        }
        if (!isFieldVisible(f, formData) && ['monthlySalary', 'dailyWage', 'tripCommission'].includes(f.name)) {
          payload[f.name] = 0;
        }
      });

      if (payload.vendorId && !payload.vendorName) {
        const match = vendorOptions.find((v) => v._id === String(payload.vendorId));
        if (match?.label) payload.vendorName = match.label;
      }
      // When vehicle picker is used, also store Licence Plate No. for list/reports
      if (payload.vehicle && !payload.vehicleNumber) {
        const match = vehicleOptions.find((v) => v._id === String(payload.vehicle));
        if (match?.label) payload.vehicleNumber = match.label;
      }
      if (config.key === 'fastag' && !payload.group) {
        payload.group = 'Default';
      }

      if (editId) {
        await api.put(`${config.apiPath}/${editId}`, payload);
        setSuccess('Updated successfully');
        showToast('Record updated successfully');
      } else {
        await api.post(config.apiPath, payload);
        setSuccess('Created successfully');
        showToast('Record created successfully');
      }
      setOpen(false);
      fetchRows();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg || 'Operation failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: Record<string, unknown>) => {
    if (!window.confirm('Delete this record?')) return;
    try {
      await api.delete(`${config.apiPath}/${row._id}`);
      setSuccess('Deleted successfully');
      showToast('Record deleted');
      fetchRows();
    } catch { setError('Delete failed'); }
  };

  const handleBulkDelete = async (ids: string[]) => {
    await Promise.all(ids.map((id) => api.delete(`${config.apiPath}/${id}`)));
    setSuccess(`Deleted ${ids.length} record(s)`);
    showToast(`${ids.length} record(s) deleted`);
    fetchRows();
  };

  const handleBulkStatus = async (ids: string[], status: string) => {
    await Promise.all(ids.map((id) => api.put(`${config.apiPath}/${id}`, { status })));
    setSuccess(`${ids.length} record(s) marked ${status}`);
    showToast(`${ids.length} record(s) updated to ${status}`);
    fetchRows();
  };

  const openDetail = (row: Record<string, unknown>) => {
    setDetailRow(row);
    setDetailOpen(true);
  };

  const handleRowClick = (row: Record<string, unknown>) => {
    if (config.key === 'vehicles' || config.key === 'drivers' || config.key === 'helpers') {
      navigate(`/${config.key}/${row._id}`);
      return;
    }
    if (canView) openDetail(row);
  };

  const tableColumns = useTableColumns(config.key, config.columns, config.fields);

  return (
    <Box>
      <PageHeader
        title={config.title}
        subtitle={getPageSubtitle(
          config.key,
          config.title,
          { total: systemTotal != null ? systemTotal : rows.length },
        )}
        icon={getModuleIcon(config.icon)}
        isCore={isCore}

        action={
          canCreate ? (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => { resetForm(); setOpen(true); }}
              sx={{
                px: 3, py: 1.2, fontWeight: 700, borderRadius: `${RADIUS}px`,
                background: brandCss.gradientPrimary,
                boxShadow: brandCss.shadowButtonLg,
              }}>
              Add New {({
                clients: 'Client',
                drivers: 'Driver',
                vehicle_vendors: 'Vehicle Vendor',
                helpers: 'Helper',
                vehicles: 'Vehicle',
                branches: 'Branch',
                consignors: 'Sender',
                consignees: 'Receiver',
              } as Record<string, string>)[config.key] || config.title}
            </Button>
          ) : undefined
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {usesVehicleFleet && !vehicleFleet.loading && vehicleFleet.typeOptions.length === 0 && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          No vehicle types in master data yet. Super Admin must add types, makes and models under Master Data.
        </Alert>
      )}
      {usesVehicleFleet && !vehicleFleet.loading && vehicleFleet.typeOptions.length > 0 && vehicleFleet.modelCount === 0 && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
          Vehicle models are empty. Super Admin must add models under Master Data → Vehicle Models (select make and type).
        </Alert>
      )}

      <DataTable
        columns={tableColumns.columns}
        columnPicker={{
          allColumns: tableColumns.allColumns,
          visibleFields: tableColumns.activeFields,
          defaultFields: tableColumns.defaultFields,
          onSave: tableColumns.updateColumns,
          onReset: tableColumns.resetColumns,
          isCustomized: tableColumns.isCustomized,
        }}
        rows={rows}
        loading={loading}
        search={search}
        onSearchChange={(v) => { setSearch(v); setPage(0); }}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        onView={canView ? openDetail : undefined}
        onEdit={canEdit ? openEdit : undefined}
        onDelete={canDelete ? handleDelete : undefined}
        onRowClick={handleRowClick}
        onBulkDelete={canDelete ? handleBulkDelete : undefined}
        onBulkStatusChange={canEdit ? handleBulkStatus : undefined}
        onExport={canExport ? async () => {
          try {
            const res = await api.get(`${config.apiPath}/export`, { responseType: 'blob' });
            const url = URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a'); a.href = url; a.download = `${config.key}.csv`; a.click();
          } catch { setError('Export failed'); }
        } : undefined}
        onImport={canImport ? async (records) => {
          try {
            const res = await api.post(`${config.apiPath}/import`, { records });
            setSuccess(`Imported: ${res.data.results?.created ?? records.length} records`);
            fetchRows();
          } catch { setError('Import failed'); }
        } : undefined}
        canView={canView}
        canEdit={canEdit}
        canDelete={canDelete}
        canImport={canImport}
        canExport={canExport}
        enableExcelExport={canExport}
        enableColumnFilters
        exportFileName={config.key}
        exportSheetName={config.title}
        title={config.title}
        recordLabel={config.title.toLowerCase()}
        searchPlaceholder="Search..."
        showRowNumber
        statusOptions={
          config.key === 'vehicles'
            ? [
              { label: 'Status', value: 'all' },
              { label: 'Active', value: 'active' },
              { label: 'Maintenance', value: 'maintenance' },
              { label: 'Inactive', value: 'inactive' },
            ]
            : config.fields.some((f) => f.name === 'status')
              ? [
                { label: 'Status', value: 'all' },
                ...(config.fields.find((f) => f.name === 'status')?.options || [
                  { label: 'Active', value: 'active' },
                  { label: 'Inactive', value: 'inactive' },
                ]),
              ]
              : undefined
        }
        toolbarFilters={buildConfiguredToolbarFilters(
          tableColumns.activeFilterFields,
          config.columns,
          rows,
          Object.fromEntries(tableColumns.filterOptions.map((o) => [o.field, o.header])),
        )}
      />

      <RecordDetailDrawer
        open={detailOpen}
        onClose={() => { setDetailOpen(false); setDetailRow(null); }}
        row={detailRow}
        fields={config.fields}
        moduleTitle={config.title.replace(/s$/, '')}
        canEdit={canEdit}
        canDelete={canDelete}
        onEdit={() => {
          if (detailRow) {
            setDetailOpen(false);
            openEdit(detailRow);
          }
        }}
        onDelete={() => {
          if (detailRow) {
            setDetailOpen(false);
            handleDelete(detailRow);
            setDetailRow(null);
          }
        }}
      />

      <StylishFormModal
        open={open}
        onClose={() => setOpen(false)}
        onSubmit={handleSubmit}
        title={config.key === 'fuel' ? (editId ? 'Edit Fuel Details' : 'Create Fuel Details') : config.title.replace(/s$/, '')}
        subtitle={config.key === 'fuel' ? 'Record vehicle fuel fill-up with employee and station details' : `Fill in the details below to ${editId ? 'update' : 'create'} a record`}
        fields={config.fields}
        formData={formData}
        onChange={setFormData}
        isEdit={!!editId}
        loading={saving}
        clientOptions={clientOptions}
        vehicleOptions={vehicleOptions}
        userOptions={userOptions}
        vendorOptions={vendorOptions}
        vehicleFleet={usesVehicleFleet ? {
          typeOptions: vehicleFleet.typeOptions,
          makesForType: vehicleFleet.makesForType,
          modelsForMake: vehicleFleet.modelsForMake,
          loading: vehicleFleet.loading,
        } : undefined}
        onImageUpload={async (file, fieldName) => {
          const fd = new FormData();
          fd.append('file', file);
          fd.append('type', fieldName);
          fd.append('folder', config.key);
          const res = await assetAPI.upload(fd);
          return res.data.url;
        }}
      />
    </Box>
  );
};
